import { useEffect, useRef, type Ref } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Clapperboard, Play } from 'lucide-react';

import { HighlightedSelections, highlightMarkClassName } from '@/components/HighlightedSelections';
import { PhraseChipRow, usePhraseChips } from '@/components/PhraseChips';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useCardVideos } from '@/hooks/useCardVideos';
import { VideoPlayerWithControls } from '@/components/videos/VideoPlayerWithControls';
// `maxresdefault` is missing for plenty of videos — YouTube then serves a grey 120x90
// placeholder with a 200, which no error handler can catch. At 84px wide the always
// present `mqdefault` still renders sharp.
import { useYoutubeOEmbed, youtubeFallbackThumbnailUrl } from '@/hooks/useYoutubeOEmbed';
import type { PhraseChip, PhraseTokenDto, YoutubeSearchItem } from '@/services';
import { cn } from '@/services/utils';

const formatTimestamp = (milliseconds: number) => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const toSelections = (item: YoutubeSearchItem) =>
  (item.highlightSpans ?? [])
    .map((span) => ({ start: span.start, length: span.end - span.start }))
    .filter((selection) => selection.length > 0);

function VideoSkeletonRow({ animated }: { animated: boolean }) {
  return (
    <div className="flex items-center gap-3 border-b border-slate-100 py-3 last:border-b-0">
      <Skeleton animated={animated} className="aspect-video w-[84px] shrink-0 rounded-lg" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton animated={animated} className="h-[11px] w-[85%]" />
        <Skeleton animated={false} className="h-2.5 w-[45%] bg-slate-50" />
      </div>
    </div>
  );
}

function VideoLoadingSkeleton() {
  return (
    <div aria-hidden="true">
      {[0, 1, 2, 3].map((index) => (
        <VideoSkeletonRow key={index} animated={index < 2} />
      ))}
    </div>
  );
}

function VideoQueueRow({
  item,
  active,
  onSelect,
  ref,
}: {
  item: YoutubeSearchItem;
  active: boolean;
  onSelect: () => void;
  ref?: Ref<HTMLButtonElement>;
}) {
  const { metadata } = useYoutubeOEmbed(item.videoId);
  const caption = metadata ? [metadata.title, metadata.channel].filter(Boolean).join(' · ') : '';

  return (
    <button
      ref={ref}
      type="button"
      onClick={onSelect}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'flex w-full items-center gap-3 rounded-xl p-2 text-left transition',
        active ? 'bg-[#e7f0e8]' : 'hover:bg-slate-50'
      )}
    >
      <span className="relative aspect-video w-[84px] shrink-0 overflow-hidden rounded-lg bg-slate-200">
        <img
          src={youtubeFallbackThumbnailUrl(item.videoId)}
          alt=""
          loading="lazy"
          onError={(event) => event.currentTarget.classList.add('invisible')}
          className="size-full object-cover"
        />
        <span className="absolute inset-0 flex items-center justify-center bg-slate-950/15">
          <span className="flex size-[22px] items-center justify-center rounded-full bg-white/90">
            <Play className="size-2.5 fill-slate-800 text-slate-800" />
          </span>
        </span>
        <span className="absolute bottom-1 right-1 rounded bg-slate-950/70 px-1 text-[9px] font-semibold tabular-nums text-white">
          {formatTimestamp(item.playbackStartMilliseconds)}
        </span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-stretch gap-2.5">
          <span
            aria-hidden
            className={cn('w-1 shrink-0 self-stretch rounded-sm', active ? 'bg-[#496653]' : 'bg-red-400')}
          />
          <span className="min-w-0 flex-1 truncate text-[15px] italic text-slate-800">
            “
            <HighlightedSelections
              text={item.sentence}
              selections={toSelections(item)}
              renderHighlight={(highlighted) => (
                <mark className={cn(highlightMarkClassName, 'not-italic')}>{highlighted}</mark>
              )}
            />
            ”
          </span>
        </span>
        <span className="mt-1 block truncate text-xs text-slate-500">{caption}</span>
      </span>
    </button>
  );
}

interface CardVideosPanelProps {
  meaningId: string;
  term: string;
  initialChips?: readonly PhraseChip[];
  phraseTokens?: readonly PhraseTokenDto[];
  open: boolean;
  panelId?: string;
}

export function CardVideosPanel({
  meaningId,
  term,
  initialChips,
  phraseTokens,
  open,
  panelId = 'card-videos-panel',
}: CardVideosPanelProps) {
  const reduceMotion = useReducedMotion();
  const { chipStates, setChipState } = usePhraseChips(meaningId);

  const {
    items,
    chips,
    atDefault,
    activeItem,
    activeIndex,
    canMoveNext,
    loading,
    loadingMore,
    error,
    loadMoreError,
    captions,
    captionsLoading,
    captionsError,
    goToNext,
    goToPrevious,
    goToIndex,
    retry,
  } = useCardVideos(meaningId, { enabled: open, initialChips, phraseTokens, chipStates });
  const activeRowRef = useRef<HTMLButtonElement>(null);
  const videoListRef = useRef<HTMLDivElement>(null);

  // The whole result list stays on screen, so walking forward has to bring the row
  // that is playing back into view. Scroll only the list's own scroll container —
  // native scrollIntoView also nudges outer ancestors (the panel, the page) to
  // reveal the row, which reads as the whole screen shifting when the video's
  // aspect ratio leaves less room for the list.
  useEffect(() => {
    const row = activeRowRef.current;
    const container = videoListRef.current;
    if (!row || !container) return;

    const containerRect = container.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const rowTop = rowRect.top - containerRect.top + container.scrollTop;
    const rowBottom = rowTop + rowRect.height;
    const viewTop = container.scrollTop;
    const viewBottom = viewTop + container.clientHeight;

    let top: number | null = null;
    if (rowTop < viewTop) {
      top = rowTop;
    } else if (rowBottom > viewBottom) {
      top = rowBottom - container.clientHeight;
    }
    if (top !== null) {
      container.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }, [activeIndex, reduceMotion]);

  if (!open) return null;

  return (
    <div id={panelId} className="flex h-full min-h-0 flex-col">
      <div className="shrink-0">
        <p id={`${panelId}-description`} className="sr-only">
          Where this card is spoken in the ingested subtitles. Each chip carries a word and the part
          of speech it is held to; relax either, or switch the chip off, to search more widely and
          see all matching sentences.
        </p>
        {chips.length > 0 && (
          <div className="border-b border-[#f1eee8] px-[18px] py-3">
            <PhraseChipRow
              chips={chips}
              states={chipStates}
              onChipChange={setChipState}
              label="Filter video matches"
            />
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto px-5 py-4">
        {loading && <VideoLoadingSkeleton />}

        {!loading && error && (
          <div role="alert" className="flex flex-col gap-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between">
            <span>{error}</span>
            <Button
              type="button"
              variant="outline"
              onClick={retry}
              className="h-8 shrink-0 rounded-full border-red-200 bg-white text-red-700 hover:bg-red-100"
            >
              Try again
            </Button>
          </div>
        )}

        {!loading && !error && !activeItem && (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-slate-200 bg-white/70 py-10 text-center">
            <span className="flex size-12 items-center justify-center rounded-full bg-[#e7f0e8] text-[#496653]">
              <Clapperboard className="size-6" />
            </span>
            <p className="mt-4 font-semibold text-slate-800">No videos yet</p>
            <p className="mt-1 px-4 text-sm leading-6 text-slate-500">
              {atDefault
                ? `Nothing in the ingested subtitles matches “${term}” right now.`
                : 'No subtitles match the selected words.'}
            </p>
          </div>
        )}

        {!loading && activeItem && (
          <div className="flex min-h-0 flex-1 flex-col gap-4">
            <VideoPlayerWithControls
              videoId={activeItem.videoId}
              playbackStartMilliseconds={activeItem.playbackStartMilliseconds}
              sentenceId={activeItem.sentenceId}
              highlightSpans={activeItem.highlightSpans ?? []}
              captions={captions}
              captionsLoading={captionsLoading}
              captionsError={captionsError}
              activeIndex={activeIndex}
              canMoveNext={canMoveNext}
              loadingMore={loadingMore}
              loadMoreError={loadMoreError}
              onPrevious={goToPrevious}
              onNext={() => void goToNext()}
              overlay={
                <span className="pointer-events-none absolute left-2.5 top-2.5 rounded-full bg-slate-950/55 px-2.5 py-1 text-xs font-bold uppercase tracking-[0.04em] text-white">
                  Now playing
                </span>
              }
              frameClassName="-mx-5"
              captionClassName="-mx-5 rounded-none border-x-0"
            />

            {items.length > 0 && (
              <div className="-mx-5 min-h-0 flex-1 border-t border-slate-100 px-4 pt-3">
                <div ref={videoListRef} className="flex h-full flex-col gap-1 overflow-y-auto">
                  {items.map((item, index) => (
                    <VideoQueueRow
                      key={item.sentenceId}
                      ref={index === activeIndex ? activeRowRef : undefined}
                      item={item}
                      active={index === activeIndex}
                      onSelect={() => goToIndex(index)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
